const CartStore = {
  state: null,

  // The badge carrying the drawer's own figure, and the attribute with which the
  // section declares that the figure omits gift units.
  DISPLAY_COUNT_SELECTOR: '[data-cart-display-count]',
  EXCLUDES_GIFTS_ATTRIBUTE: 'data-cart-count-excludes-gifts',

  _subscribers: [],
  _initPromise: null,

  // Last counts written to the badges, server-sourced or predicted. Held separately
  // from `state.item_count` so consecutive predictions compose without ever
  // rewriting the cart JSON, which stays server-authored. The display count is a
  // second figure because the drawer badge may be rendered excluding gift units —
  // cart.js carries no gift knowledge, so it can only ever come from Liquid.
  _predictedCount: null,
  _predictedDisplayCount: null,

  _notify() {
    for (const fn of this._subscribers) fn(this.state);
  },

  init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = CartAPI.get()
      .then((data) => {
        this.state = data;
        this._predictedCount = data.item_count ?? this._predictedCount;
        // Seeded from the server-rendered attribute, once: from here on every drawer
        // morph re-supplies it, and a prediction composes on top of the last value.
        this._predictedDisplayCount ??= this._renderedDisplayCount();
        this._notify();
      })
      .catch((error) => {
        console.error('[CartStore] init failed:', error);
        this._initPromise = null;
      });
    return this._initPromise;
  },

  sync(cartData) {
    this.state = cartData;
    this._predictedCount = cartData.item_count ?? this._predictedCount;
    this._notify();
  },

  subscribe(fn) {
    this._subscribers.push(fn);
    fn(this.state);
    return () => {
      this._subscribers = this._subscribers.filter((s) => s !== fn);
    };
  },

  // Takes counts rather than a cart object — after an add.js mutation the only
  // authoritative figures are the ones the morphed header carries. `displayCount` is
  // null when the caller has no Liquid-computed figure to offer, in which case the
  // display badge is left alone: a mutation whose morph never rendered the drawer
  // header cannot speak for it.
  reconcile(itemCount, displayCount = null) {
    this._predictedCount = itemCount;

    for (const el of document.querySelectorAll('[data-cart-count]')) {
      if (el.textContent !== String(itemCount)) el.textContent = itemCount;
      el.hidden = itemCount === 0;
    }

    if (displayCount === null) return;

    this._predictedDisplayCount = displayCount;

    // Never hidden at zero, unlike [data-cart-count]: the drawer badge is part of
    // the header's fixed layout and reads "0" on an empty cart by design.
    for (const el of document.querySelectorAll(this.DISPLAY_COUNT_SELECTOR)) {
      if (el.textContent !== String(displayCount)) el.textContent = displayCount;
    }
  },

  // Both badges sit outside every morph target on at least one surface, so a
  // quantity change would otherwise leave them stale for a whole round trip. Routed
  // through `reconcile` rather than writing the DOM here, so that stays the single
  // writer. No-ops until the store knows a real count — guessing from an unknown
  // baseline is worse than waiting for the morph.
  predictCount(delta, { isGift = false } = {}) {
    if (this._predictedCount === null) return;

    let nextDisplay = null;
    if (this._predictedDisplayCount !== null) {
      const displayDelta = isGift && this._displayExcludesGifts() ? 0 : delta;
      nextDisplay = Math.max(0, this._predictedDisplayCount + displayDelta);
    }

    this.reconcile(Math.max(0, this._predictedCount + delta), nextDisplay);
  },

  _renderedDisplayCount() {
    const badge = document.querySelector(this.DISPLAY_COUNT_SELECTOR);
    const parsed = Number.parseInt(badge?.dataset.cartDisplayCount ?? '', 10);
    return Number.isNaN(parsed) ? null : parsed;
  },

  // A gift must move the total without moving a badge the section rendered excluding
  // gift units. Read off the badge rather than held in JS — the setting is the
  // section's, and every morph re-stamps it.
  _displayExcludesGifts() {
    const badge = document.querySelector(this.DISPLAY_COUNT_SELECTOR);
    return badge?.hasAttribute(this.EXCLUDES_GIFTS_ATTRIBUTE) ?? false;
  },
};

window.CartStore = CartStore;
